import {
  ArrowLeftSmallIcon,
  ArrowRightSmallIcon,
  ArrowsLeftIcon,
  ArrowsRightIcon,
} from '@blocksuite/icons/rc';
import clsx from 'clsx';
import dayjs from 'dayjs';
import type { ForwardedRef, HTMLAttributes } from 'react';
import {
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { observeResize } from '../../utils';
import { IconButton } from '../button';
import * as styles from './week-date-picker.css';

export interface WeekDatePickerHandle {
  /** control cursor manually */
  setCursor?: (cursor: dayjs.Dayjs) => void;
}

export interface WeekDatePickerProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'onChange'
> {
  value?: string;
  onChange?: (value: string) => void;
  handleRef?: ForwardedRef<WeekDatePickerHandle>;
  weekStartsOn?: 'monday' | 'sunday';
}

// TODO(catsjuice): i18n
const weekDays = {
  monday: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  sunday: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
} as const;
// const weekDays = ['日', '一', '二', '三', '四', '五', '六'];
const format = 'YYYY-MM-DD';
type WeekStartsOn = NonNullable<WeekDatePickerProps['weekStartsOn']>;

const getDayIndex = (day: dayjs.Dayjs, weekStartsOn: WeekStartsOn) =>
  weekStartsOn === 'monday' ? (day.day() + 6) % 7 : day.day();

const getWeekStart = (day: dayjs.Dayjs, weekStartsOn: WeekStartsOn) =>
  day.subtract(getDayIndex(day, weekStartsOn), 'day').startOf('day');

const getWeekEnd = (day: dayjs.Dayjs, weekStartsOn: WeekStartsOn) =>
  getWeekStart(day, weekStartsOn).add(6, 'day').startOf('day');

const isSameWeek = (
  a: dayjs.Dayjs,
  b: dayjs.Dayjs,
  weekStartsOn: WeekStartsOn
) => getWeekStart(a, weekStartsOn).isSame(getWeekStart(b, weekStartsOn), 'day');

export const WeekDatePicker = memo(function WeekDatePicker({
  value,
  onChange,
  className,
  handleRef,
  weekStartsOn = 'monday',
  ...attrs
}: WeekDatePickerProps) {
  const weekRef = useRef<HTMLDivElement | null>(null);

  const [cursor, setCursor] = useState(dayjs(value));
  const [dense, setDense] = useState(false);
  const [viewPortSize, setViewPortSize] = useState(7);

  useImperativeHandle(handleRef, () => ({
    setCursor,
  }));

  const range = useMemo(() => {
    if (viewPortSize === 7) return [0, 7];
    const cursorIndex = getDayIndex(cursor, weekStartsOn);
    let start = Math.max(0, cursorIndex - Math.floor(viewPortSize / 2));
    const end = Math.min(7, start + viewPortSize);
    if (end === 7) start = 7 - viewPortSize;
    return [start, end];
  }, [cursor, viewPortSize, weekStartsOn]);
  const allDays = useMemo(
    () =>
      Array.from({ length: 7 }).map((_, index) => {
        return getWeekStart(cursor, weekStartsOn).add(index, 'day');
      }),
    [cursor, weekStartsOn]
  );
  const displayDays = useMemo(() => allDays.slice(...range), [allDays, range]);

  const onNextWeek = useCallback(() => {
    const viewPortSize = displayDays.length;
    if (viewPortSize === 7) setCursor(c => c.add(1, 'week'));
    else {
      setCursor(c => {
        // last day of week is visible, move to next weeks
        if (
          displayDays[displayDays.length - 1].isSame(
            getWeekEnd(c, weekStartsOn),
            'day'
          )
        ) {
          return getWeekStart(c.add(1, 'week'), weekStartsOn).add(
            Math.floor(viewPortSize / 2),
            'day'
          );
        }
        let nextDay = c.add(viewPortSize, 'day');
        if (!isSameWeek(nextDay, c, weekStartsOn)) {
          nextDay = getWeekEnd(c, weekStartsOn);
        }
        return nextDay;
      });
    }
  }, [displayDays, weekStartsOn]);
  const onPrevWeek = useCallback(() => {
    const viewPortSize = displayDays.length;
    if (viewPortSize === 7) setCursor(c => c.add(-1, 'week'));
    else
      setCursor(c => {
        // first day of week is visible, move to prev weeks
        if (displayDays[0].isSame(getWeekStart(c, weekStartsOn), 'day')) {
          return getWeekEnd(c.subtract(1, 'week'), weekStartsOn).subtract(
            Math.floor(viewPortSize / 2) - 1,
            'day'
          );
        }
        let prevDay = c.add(-viewPortSize, 'day');
        if (!isSameWeek(prevDay, c, weekStartsOn)) {
          prevDay = getWeekStart(c, weekStartsOn);
        }
        return prevDay;
      });
  }, [displayDays, weekStartsOn]);
  const onPrevDay = useCallback(() => {
    const base = value ? dayjs(value) : cursor;
    const next = base.add(-1, 'day');
    setCursor(next);
    onChange?.(next.format(format));
  }, [value, cursor, onChange]);
  const onNextDay = useCallback(() => {
    const base = value ? dayjs(value) : cursor;
    const next = base.add(1, 'day');
    setCursor(next);
    onChange?.(next.format(format));
  }, [value, cursor, onChange]);
  const onDayClick = useCallback(
    (day: dayjs.Dayjs) => {
      onChange?.(day.format(format));
    },
    [onChange]
  );

  // Observe weekRef to update viewPortSize
  useEffect(() => {
    const el = weekRef.current;
    if (!el) return;

    return observeResize(el, ({ contentRect: rect }) => {
      const width = rect.width;
      if (!width) return;

      const minWidth = 30;
      const gap = 4;
      const viewPortCount = Math.floor(width / (minWidth + gap));
      setViewPortSize(Math.max(1, Math.min(viewPortCount, 7)));
      setDense(width < 300);
    });
  }, []);

  // when value changes, reset cursor
  useEffect(() => {
    value && setCursor(dayjs(value));
  }, [value]);

  const focusCursorCell = useCallback(() => {
    if (!weekRef.current) return;
    const cursorCell = weekRef.current.querySelector(
      'button[tabIndex="0"]'
    ) as HTMLButtonElement;
    cursorCell?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!weekRef.current) return;
    const el = weekRef.current;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      e.stopPropagation();
      setCursor(cursor => cursor.add(e.key === 'ArrowLeft' ? -1 : 1, 'day'));
      setTimeout(focusCursorCell);
    };

    el.addEventListener('keydown', onKeyDown);

    return () => {
      el.removeEventListener('keydown', onKeyDown);
    };
  }, [focusCursorCell, onNextWeek, onPrevWeek]);

  return (
    <div className={clsx(styles.weekDatePicker, className)} {...attrs}>
      <IconButton onClick={onPrevWeek} data-testid="week-picker-prev-week">
        <ArrowsLeftIcon />
      </IconButton>
      <IconButton
        onClick={onPrevDay}
        className={styles.dayArrowButton}
        data-testid="week-picker-prev"
      >
        <ArrowLeftSmallIcon />
      </IconButton>

      <div ref={weekRef} className={styles.weekDatePickerContent}>
        {displayDays.map(day => (
          <Cell
            key={day.toISOString()}
            dense={dense}
            value={value}
            day={day}
            cursor={cursor}
            weekStartsOn={weekStartsOn}
            onClick={onDayClick}
          />
        ))}
      </div>

      <IconButton
        onClick={onNextDay}
        className={styles.dayArrowButton}
        data-testid="week-picker-next"
      >
        <ArrowRightSmallIcon />
      </IconButton>
      <IconButton onClick={onNextWeek} data-testid="week-picker-next-week">
        <ArrowsRightIcon />
      </IconButton>
    </div>
  );
});

interface CellProps {
  dense: boolean;
  day: dayjs.Dayjs;
  cursor: dayjs.Dayjs;
  value?: string;
  weekStartsOn: WeekStartsOn;
  onClick: (day: dayjs.Dayjs) => void;
}
const Cell = ({
  day,
  dense,
  value,
  cursor,
  weekStartsOn,
  onClick,
}: CellProps) => {
  const isActive = day.format(format) === value;
  const isCurrentMonth = day.month() === cursor.month();
  const isToday = day.isSame(dayjs(), 'day');

  const dayIndex = getDayIndex(day, weekStartsOn);
  const label = weekDays[weekStartsOn][dayIndex];

  return (
    <button
      tabIndex={cursor.isSame(day, 'day') ? 0 : -1}
      data-testid="week-picker-day"
      aria-label={day.format(format)}
      data-active={isActive}
      data-curr-month={isCurrentMonth}
      data-today={isToday}
      data-value={day.format(format)}
      key={day.toISOString()}
      className={styles.dayCell}
      onClick={() => onClick(day)}
    >
      <div className={styles.dayCellWeek}>
        {dense ? label.slice(0, 1) : label}
      </div>
      <div className={styles.dayCellDate}>{day.format('D')}</div>
    </button>
  );
};
